import ConductorIsolation3D from './ConductorIsolation3D';
import Rope3D from './Rope3D';
import SheathLayer3D from './SheathLayer3D';
import Armour3D from './Armour3D';
import PreStranding3D from './PreStranding3D';

/**
 * The right 3D view for one phase of a cable.
 *
 * Extracted from StandardEditModal so the create wizard shows the same picture
 * of the same numbers. Both feed it a model from cableGeometry.buildCableModel,
 * so what is drawn is the geometry as currently edited — not a saved snapshot.
 *
 * It draws whatever the model has and returns a placeholder otherwise; a phase
 * being half filled in is the normal state in a wizard, not an error.
 */

/**
 * Which sheath is bedding — the one laid straight onto the rope, so it fills the
 * voids between the conductors rather than sitting on a smooth cylinder.
 * Recognised by its inner diameter matching the rope's packing circle.
 */
export const beddingKeyOf = model => {
  if (!model?.hasRope) return null;
  const L = model.cableLayers.find(
    x => x.kind === 'sheath' && Math.abs(x.innerD - model.ropePackingD) < 0.3
  );
  return L?.key || null;
};

const Phase3D = ({ phase, model, beddingKey, productColor, height = 300 }) => {
  if (!model || !phase) return null;

  // A layer with no size yet is the normal state halfway through a wizard, and
  // handing zero-radius geometry to three.js gets a blank canvas or a warning
  // rather than a drawing. Say so instead.
  if (phase.name === 'conductor_isolation') {
    if (!model.cores?.some(c => Number(c.outerD) > 0)) return <Empty height={height} />;
    return <ConductorIsolation3D model={model} height={height} />;
  }

  if (phase.name === 'rope') {
    if (!model.hasRope || !(Number(model.ropeDiameter) > 0)) return <Empty height={height} />;
    return <Rope3D model={model} height={height} />;
  }

  if (phase.name === 'interior_sheat' || phase.name === 'exterior_sheat') {
    const L = model.cableLayers.find(x => x.key === phase.name);
    if (!L || !(Number(L.outerD) > 0)) return <Empty height={height} />;
    return (
      <SheathLayer3D
        layer={L}
        productColor={productColor}
        bedding={phase.name === beddingKey && model.hasRope}
        cores={model.cores}
        numberOfConductors={model.numberOfConductors}
        ropeDiameter={model.ropeDiameter}
        // A multi-core cable with no rope phase is laid flat side by side, not
        // twisted — MYYUP's two cores under one sheath.
        flat={!model.hasRope && (model.numberOfConductors || model.cores.length) > 1}
        height={height}
      />
    );
  }

  if (phase.name === 'armouring') {
    const L = model.cableLayers.find(x => x.key === 'armouring');
    if (!L || !(Number(L.outerD) > 0)) return <Empty height={height} />;
    return <Armour3D layer={L} height={height} />;
  }

  if (phase.name === 'pre_stranding') {
    // Two shapes in the data: a rope[] array on cables that strand more than one
    // build, and the fields flat on the phase otherwise. Both get drawn.
    const ropes = Array.isArray(phase.rope) && phase.rope.length ? phase.rope : [phase];
    const drawable = ropes.filter(r => Number(r?.diameter) > 0 || Number(r?.wireDiameter?.[0]) > 0);
    if (!drawable.length) return <Empty height={height} />;
    return (
      <>
        {drawable.map((r, i) => (
          <div key={i} className={i ? 'mt-2' : ''}>
            <PreStranding3D rope={r} height={height} />
          </div>
        ))}
      </>
    );
  }

  return <Empty height={height} />;
};

const Empty = ({ height }) => (
  <div
    style={{ height, background: '#0f1420', borderRadius: 6 }}
    className="d-flex align-items-center justify-content-center text-secondary small"
  >
    Completează diametrele pentru vizualizare
  </div>
);

export default Phase3D;
